import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { AppContexts } from "../../providers";
import { getAnnouncementFeature } from "../features/catalog";
import { getAnnouncementGuideUrl } from "../../content/help/catalog";
import { SubscriptionRequirement } from "../features/shared";
import "../features/features.css";
import { getAnnouncementDate, getReleaseDate, isNewAnnouncement, safeExternalUrl } from "./model";

const AnnouncementCard = ({ announcement, dismissible = false, anchored = false }) => {
  const { user } = useContext(AppContexts.AuthContext);
  const { dismissAnnouncement } = useContext(AppContexts.AnnouncementsContext);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const title = (announcement.title || "").replace(/^new feature:\s*/i, "").trim();
  const feature = getAnnouncementFeature(announcement);
  const date = getAnnouncementDate(announcement);
  const helpUrl = safeExternalUrl(announcement.link);
  const localGuideUrl = getAnnouncementGuideUrl(announcement);
  const videoUrl = safeExternalUrl(announcement.video_url);
  const imageUrl = safeExternalUrl(announcement.image_url);

  const dismiss = async () => {
    if (pending) return;
    setPending(true);
    setError("");
    try {
      await dismissAnnouncement(user?.user?.user_id || user?.user_id, announcement.id);
    } catch {
      setError("We couldn’t dismiss this notice. Please try again.");
    } finally {
      setPending(false);
    }
  };

  return (
    <article id={anchored ? `announcement-${announcement.id}` : undefined} className="rbr-feature-card rbr-announcement-card">
      {dismissible && <button type="button" className="rbr-announcement-dismiss" disabled={pending} onClick={dismiss} aria-label={`Dismiss notice: ${title}`}>×</button>}
      {imageUrl && <img className="rbr-whats-new-image" src={imageUrl} alt="" />}
      {(feature?.category || announcement.feature_category) && <span className="rbr-discovery-eyebrow">{feature?.category || announcement.feature_category}</span>}
      <div className="rbr-announcement-heading"><h2>{localGuideUrl ? <Link to={localGuideUrl}>{title}</Link> : title}</h2>{isNewAnnouncement(announcement) && <span className="rbr-whats-new-badge">New</span>}</div>
      {date && moment(date).isValid() && <div className="rbr-whats-new-date">{getReleaseDate(announcement) ? "Released" : "Announced"}: <time dateTime={moment(date).format("YYYY-MM-DD")}>{moment(date).format("MMMM D, YYYY")}</time></div>}
      <p className="rbr-feature-benefit">{announcement.description}</p>
      {feature && <SubscriptionRequirement feature={feature} className={feature.requirement === "texting" ? "uk-margin-small-top" : ""} />}
      <div className="hms-guest-btn">
        {localGuideUrl ? <Link className="uk-button hms-btn hms-red-btn uk-flex-inline uk-flex-middle" to={localGuideUrl}>Learn more</Link> : helpUrl ? <a className="uk-button hms-btn hms-red-btn uk-flex-inline uk-flex-middle" href={helpUrl} target="_blank" rel="noopener noreferrer">Learn more ↗</a> : feature && <Link className="uk-button hms-btn hms-red-btn uk-flex-inline uk-flex-middle" to={`/features/${feature.id}`}>Learn more</Link>}
        {videoUrl && <a className="uk-button hms-btn hms-white-btn uk-flex-inline uk-flex-middle" href={videoUrl} target="_blank" rel="noopener noreferrer">Watch video ↗</a>}
        {!feature && !helpUrl && !localGuideUrl && <Link className="uk-button hms-btn hms-red-btn uk-flex-inline uk-flex-middle" to="/features">Explore all features →</Link>}
      </div>
      {error && <p className="rbr-discovery-error" role="alert">{error}</p>}
    </article>
  );
};

export default AnnouncementCard;
